/**
 * The app's active theme: one context, one root, one set of CSS variables.
 *
 * The *selected* theme is workspace data (`UserWorkspace.themeRef`, D13) — this
 * module only resolves that reference and publishes the result two ways,
 * because two kinds of consumer need it in two different shapes:
 *
 * - **CSS variables** on the shell root, for chrome. Style objects here are
 *   module-level constants that cannot call a hook, so they read
 *   `var(--qf-surface)` and inherit. A theme switch is then a single inline
 *   style change on one element — a repaint, not a re-mount.
 * - **The `Theme` object**, via context, for the renderers that need real
 *   values: the chart builder (which maps tokens onto SciChart brushes) and the
 *   screener island (which takes an injected `resolveToken`).
 *
 * Nothing here persists anything. Selecting a theme dispatches a workspace
 * action; this component just reflects whatever the workspace currently says.
 */

import { createContext, useContext, useEffect, useMemo, type ReactNode } from "react";
import {
  DEFAULT_THEME,
  themeById,
  themeCssVariables,
  tokenResolverFor,
  type Theme,
} from "@qufolio/model";

import { BRAND } from "../lib/brand-current";
import { brandDefaultTheme } from "../lib/brand-themes";
import { desktopBridge } from "../lib/desktop";

const ThemeContext = createContext<Theme>(DEFAULT_THEME);

/** The active theme. Defaults to Classic outside a provider. */
export function useTheme(): Theme {
  return useContext(ThemeContext);
}

/**
 * A `(token) => css` resolver bound to the active theme.
 *
 * Memoised on the theme so its identity is stable: `ScreenerGrid` keys its
 * column-definition memo on this function, so a new identity per render would
 * rebuild every column on every render — and a *changed* identity is exactly
 * how a theme switch re-resolves conditional formatting colors.
 */
export function useTokenResolver(): (token: string) => string {
  const theme = useTheme();
  return useMemo(() => tokenResolverFor(theme), [theme]);
}

export interface ThemeProviderProps {
  /** `UserWorkspace.themeRef` — unknown or absent resolves to the default. */
  themeRef?: string;
  /**
   * Themes this user authored, searched before the built-ins.
   *
   * `themeById` only knows the built-ins, so without this an authored theme
   * would be selectable and then render as the default — the tick would move
   * and nothing else would.
   */
  themes?: readonly Theme[];
  children: ReactNode;
}

export function ThemeProvider({ themeRef, themes = [], children }: ThemeProviderProps) {
  /**
   * Authored first, then the brand's own, then the built-ins, then the brand's
   * default.
   *
   * **A theme a workspace already names always resolves, offered or not.** The
   * brand's `themes.allow` narrows what the *picker lists*; it must not decide
   * what a stored `themeRef` means, because silently repainting somebody's
   * saved workspace is worse than listing one theme fewer than they remember.
   * So this searches `themeById` — every built-in — rather than the filtered
   * catalogue, and only the *absence* of a ref falls through to the brand.
   */
  const theme = useMemo(() => {
    const named =
      themes.find((t) => t.id === themeRef) ??
      BRAND.themes.custom.find((t) => t.id === themeRef) ??
      (themeRef ? themeById(themeRef) : undefined);
    return named ?? brandDefaultTheme(themes);
  }, [themeRef, themes]);
  const vars = useMemo(() => themeCssVariables(theme), [theme]);

  // In the desktop app the tab row is the title bar, and the window buttons over
  // its end are drawn by Windows: they follow a theme switch only if told.
  useEffect(() => {
    const surface = vars["--qf-surface"];
    const glyph = vars["--qf-text-secondary"];
    if (surface && glyph) desktopBridge()?.setTitleBarColors(surface, glyph);
  }, [vars]);

  return (
    <ThemeContext.Provider value={theme}>
      <div
        style={{
          ...(vars as React.CSSProperties),
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          background: "var(--qf-background)",
          color: "var(--qf-text)",
        }}
      >
        {children}
      </div>
    </ThemeContext.Provider>
  );
}
